import localforage from "localforage";
import {ModelType} from "../../type";
import {prettierLog} from "../index";
import {
    InPaintModelList,
    ModelTypeConstant,
    RemoveBgModelList,
    ReplaceClothModelList,
    StyleChangeModelList, SuperResolutionModelList
} from "../../constants";
import {
    Backup1DownloadStrategy,
    BackupDownloadStrategy,
    DownloadContext,
    PrimaryDownloadStrategy
} from "./DownloadStrategy";

localforage.config({// 标识数据库名称
    name: 'modelCache',
});

let curUsingModelIndex = {// 当前正在使用的模型在模型列表中的下标
    [ModelTypeConstant.REMOVE_BG]: 0,
    [ModelTypeConstant.STYLE_CHANGE_CLIP]: 0,
    [ModelTypeConstant.REPLACE_CLOTH]: 0,
    [ModelTypeConstant.IN_PAINT_]: 0,
    [ModelTypeConstant.SUPER_RESOLUTION]: 0,
}

let modelIndexMap = {// 当前要使用的模型在模型列表中的下标
    [ModelTypeConstant.REMOVE_BG]: 0,
    [ModelTypeConstant.STYLE_CHANGE_CLIP]: 0,
    [ModelTypeConstant.REPLACE_CLOTH]: 0,
    [ModelTypeConstant.IN_PAINT_]: 0,
    [ModelTypeConstant.SUPER_RESOLUTION]: 0,
}

export async function saveModel(modelType: ModelType, modelBlob: ArrayBuffer) {
    await localforage.setItem(getModel(modelType).name, modelBlob)
}

export function getCurModelIndex(modelType: ModelType) {
    if (modelType === ModelTypeConstant.REMOVE_BG) {
        return modelIndexMap[ModelTypeConstant.REMOVE_BG];
    }else if (modelType === ModelTypeConstant.STYLE_CHANGE_CLIP) {
        return modelIndexMap[ModelTypeConstant.STYLE_CHANGE_CLIP];
    }else if (modelType === ModelTypeConstant.REPLACE_CLOTH) {
        return modelIndexMap[ModelTypeConstant.REPLACE_CLOTH];
    }else if (modelType === ModelTypeConstant.IN_PAINT_) {
        return modelIndexMap[ModelTypeConstant.IN_PAINT_];
    }else if (modelType === ModelTypeConstant.SUPER_RESOLUTION) {
        return modelIndexMap[ModelTypeConstant.SUPER_RESOLUTION];
    }

    return 0;
}

export function setModelIndex(modelType: ModelType, modelIndex: number) {
    if (modelType === ModelTypeConstant.REMOVE_BG) {
        modelIndexMap[ModelTypeConstant.REMOVE_BG] = modelIndex;
    }else if (modelType === ModelTypeConstant.STYLE_CHANGE_CLIP) {
        modelIndexMap[ModelTypeConstant.STYLE_CHANGE_CLIP] = modelIndex;
    }else if (modelType === ModelTypeConstant.REPLACE_CLOTH) {
        modelIndexMap[ModelTypeConstant.REPLACE_CLOTH] = modelIndex;
    }else if (modelType === ModelTypeConstant.IN_PAINT_) {
        modelIndexMap[ModelTypeConstant.IN_PAINT_] = modelIndex;
    }else if (modelType === ModelTypeConstant.SUPER_RESOLUTION) {
        modelIndexMap[ModelTypeConstant.SUPER_RESOLUTION] = modelIndex;
    }
}

export function getModel(modelType: ModelType) {
    // @ts-ignore
    if (modelType === ModelTypeConstant.REMOVE_BG) {
        let index = modelIndexMap[ModelTypeConstant.REMOVE_BG];
        return RemoveBgModelList[index === -1 ? 0 : index];// 用0和2
    }else if (modelType === ModelTypeConstant.STYLE_CHANGE_CLIP) {
        let index = modelIndexMap[ModelTypeConstant.STYLE_CHANGE_CLIP];
        return StyleChangeModelList[index === -1 ? 0 : index];
    }else if (modelType === ModelTypeConstant.REPLACE_CLOTH) {
        let index = modelIndexMap[ModelTypeConstant.REPLACE_CLOTH];
        return ReplaceClothModelList[index === -1 ? 0 : index];
    }else if (modelType === ModelTypeConstant.IN_PAINT_) {
        let index = modelIndexMap[ModelTypeConstant.IN_PAINT_];
        return InPaintModelList[index === -1 ? 0 : index];
    }else if (modelType === ModelTypeConstant.SUPER_RESOLUTION) {
        let index = modelIndexMap[ModelTypeConstant.SUPER_RESOLUTION];
        return SuperResolutionModelList[index === -1 ? 0 : index];
    }

    prettierLog({
        msg: 'wrong modelType',
        type: 'error'
    });
    throw new Error('wrong modelType');
}

// 加载本地已经缓存好的模型数据
export async function loadModel(modelType: ModelType): Promise<ArrayBuffer> {
    return (await localforage.getItem(
        getModel(modelType).name
    )) as ArrayBuffer;
}

// 判断类型为modelType的本地缓存过的模型是否存在
export async function modelExists(modelType: ModelType) {
    const model = await loadModel(modelType);
    return model !== null && model !== undefined;
}

// 获取模型数据
export async function ensureModel(modelType: ModelType) {
    if (await modelExists(modelType) && curUsingModelIndex[modelType] === modelIndexMap[modelType]) {
        return loadModel(modelType);
    }

    await downloadModel(modelType);
    const model = getModel(modelType);
}
export async function downloadModel(
    modelType: ModelType,
    setDownloadProgress?: (arg0: number) => void,
    showAlert?: any
) {
    if (await modelExists(modelType)) return loadModel(modelType);// 本地已经缓存过模型数据

    async function downloadFromUrl(url: string) {
        prettierLog({
            msg: `start download from ${url}`,
            type: 'info'
        })
        if (setDownloadProgress) setDownloadProgress(0);
        const response = await fetch(url);
        const fullSize = Number(response.headers.get('content-length')) || 0;// 获取模型的大小，默认取0（服务器资源获取超时时表示大小）
        const reader = response.body!.getReader();
        const total: Uint8Array[] = [];
        let downloaded = 0;

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            downloaded += value?.length || 0;
            if (value) total.push(value);

            if (setDownloadProgress) setDownloadProgress((downloaded / Number(fullSize)) * 100);
        }

        const buffer = new Uint8Array(downloaded);
        let offset = 0;
        for (const chunk of total) {
            buffer.set(chunk, offset);
            offset += chunk.length;
        }

        await saveModel(modelType, buffer);
        if (setDownloadProgress) setDownloadProgress(100);

        return buffer;
    }

    const model = getModel(modelType);

    const downloadContext = new DownloadContext();
    if (model.url) downloadContext.addStrategy(new PrimaryDownloadStrategy(model.url));
    if (model.backupUrl) downloadContext.addStrategy(new BackupDownloadStrategy(model.backupUrl));
    if (model.backupUrl1) downloadContext.addStrategy(new Backup1DownloadStrategy(model.backupUrl1));

    return await downloadContext.executeDownload(async (url: string) => {
        let buffer= await downloadFromUrl(url);
        curUsingModelIndex[modelType] = modelIndexMap[modelType];// 设置当前正在使用的模型列表中的下标为当前要使用的模型列表中的下标
        return buffer;
    }, (e) => {
        if (setDownloadProgress) setDownloadProgress(0);// 关闭加载动画
        if (showAlert) {
            showAlert({
                message: `Failed to download the backup model: ${e}`,
                type: 'error'
            }, 3000);
        }
    });
}
